<template>
  <div
    v-if="fetchingTemplate || changingSegment"
    class="text-center text-blue-400 my-8"
  >
    <base-loading class="block text-3xl" />
    Loading
  </div>
  <div v-else class="relative flex items-start bg-white">
    <div class="sticky top-0 w-64 max-h-screen overflow-y-auto">
      <h3 class="bg-gray-300 uppercase tracking-wide px-6 py-2">
        General info
      </h3>
      <button
        @click="scrollTo('campaign')"
        class="w-full text-left px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
        :class="{
          'bg-green-100': formValues.campaign,
        }"
      >
        <strong>Campaign</strong>
        <div>{{ selectedCampaignName || '-' }}</div>
      </button>
      <button
        @click="scrollTo('target-url')"
        class="w-full text-left break-words px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
        :class="{
          'bg-green-100': formValues.target_url,
        }"
      >
        <strong>Target URL</strong>
        <div>{{ formValues.target_url || '-' }}</div>
      </button>
      <button
        @click="scrollTo('tracking-param')"
        class="w-full text-left px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
        :class="{
          'bg-green-100': formValues.tracking_param,
        }"
      >
        <strong>Tracking Parameter</strong>
        <div>{{ formValues.tracking_param || '-' }}</div>
      </button>

      <template v-if="schema.length > 0">
        <h3 class="bg-gray-300 uppercase tracking-wide px-6 py-2">
          Custom parameters
        </h3>
        <button
          v-for="parameter in schema"
          :key="parameter.id"
          @click="scrollTo(`custom-${parameter.id}`)"
          class="w-full text-left px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
          :class="{
            'bg-green-100': formValues.client_data[0][parameter.name],
          }"
        >
          <strong>{{ parameter.name }}</strong>
          <div>
            {{ formValues.client_data[0][parameter.name] || '-' }}
          </div>
        </button>
      </template>

      <h3 class="bg-gray-300 uppercase tracking-wide px-6 py-2">
        Marketer data
      </h3>
      <div v-if="fetchingMarketerParameters" class="text-blue-400 px-6 py-2">
        <base-loading class="text-xl align-middle mr-2" />
        Loading
      </div>
      <template v-else>
        <button
          v-for="parameter in marketerParameters"
          :key="parameter.name"
          @click="scrollTo(parameter.name)"
          class="w-full text-left px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
          :class="{
            'bg-green-100': parameter.values.length === 1,
          }"
        >
          <strong>{{ parameterNames[parameter.name] }}</strong>
          <div>
            {{ parameter.values.length === 1 ? parameter.values[0] : '-' }}
          </div>
        </button>
      </template>
      <div
        v-if="fetchingMarketerParametersSecondary"
        class="text-blue-400 px-6 py-2"
      >
        <base-loading class="text-xl align-middle mr-2" />
        Loading additional
      </div>
      <transition name="slide-in">
        <div
          v-if="
            !fetchingMarketerParametersSecondary &&
              marketerParametersSecondaryVisible
          "
        >
          <button
            v-for="parameter in marketerParametersSecondary"
            :key="parameter.name"
            @click="scrollTo(parameter.name)"
            class="w-full text-left px-6 py-2 transition-colors duration-100 hover:bg-blue-200"
            :class="{
              'bg-green-100': parameter.values.length === 1,
            }"
          >
            <strong>{{ parameterNames[parameter.name] }}</strong>
            <div>
              {{ parameter.values.length === 1 ? parameter.values[0] : '-' }}
            </div>
          </button>
        </div>
      </transition>
    </div>

    <FormulateForm
      :name="formName"
      @submit="createLink"
      @submit-raw="checkValidation"
      class="flex-1 bg-gray-100"
    >
      <h3 class="bg-gray-300 uppercase tracking-wide px-16 py-2">
        General info
      </h3>
      <div class="divide-y divide-gray-400">
        <div id="campaign" class="px-16 py-8">
          <FormulateInput
            v-model="formValues.campaign"
            name="campaign"
            label="Campaign *"
            validation-name="Campaign"
            type="select"
            :option-groups="campaignOptions"
            validation="required"
          />
        </div>
        <div id="target-url" class="px-16 py-8">
          <FormulateInput
            v-model="formValues.target_url"
            name="target_url"
            label="Target URL *"
            validation-name="Target URL"
            type="url"
            :error="targetUrlError"
            validation="required"
          />
        </div>
        <div id="tracking-param" class="px-16 py-8">
          <FormulateInput
            v-model="formValues.tracking_param"
            name="tracking_param"
            label="Tracking Parameter *"
            validation-name="Tracking Parameter"
            type="select"
            :options="domainParameterOptions"
            :placeholder="domainParameterPlaceholder"
            validation="required"
            :disabled="domainParameterOptions.length === 0"
          />
        </div>
      </div>

      <template v-if="schema.length > 0">
        <h3 class="bg-gray-300 uppercase tracking-wide px-16 py-2">
          Custom parameters
        </h3>
        <FormulateInput
          v-model="formValues.client_data"
          type="group"
          name="client_data"
        >
          <div
            v-for="(parameter, index) in schema"
            :key="parameter.id"
            :id="`custom-${parameter.id}`"
            class="px-16 py-8"
            :class="{
              'border-b border-solid border-gray-400':
                index !== schema.length - 1,
            }"
          >
            <FormulateInput
              :name="parameter.name"
              :label="`${parameter.name}${parameter.is_mandatory ? ' *' : ''}`"
              :type="parameter.ui_input"
              :options="parameter.options"
              :placeholder="parameter.placeholder"
              :help="parameter.help"
              :validation="parameter.validation"
              :validation-rules="parameter.validation_rules"
              :validation-messages="parameter.validation_messages"
              validation-behavior="live"
            />
          </div>
        </FormulateInput>
      </template>

      <h3 class="bg-gray-300 uppercase tracking-wide px-16 py-2">
        Marketer data
      </h3>
      <div v-if="fetchingMarketerParameters" class="text-blue-400 px-16 py-8">
        <base-loading class="block text-3xl" />
        Loading
      </div>
      <div v-else class="px-12 py-8">
        <div class="flex flex-wrap">
          <CreateLinkMarketerParameter
            v-for="parameter in marketerParameters"
            :key="parameter.name"
            :parameter="parameter"
            @input="selectParameter"
            @reset="resetParameter"
            class="w-1/3 px-4 py-2"
          />
        </div>
        <template v-if="marketerParametersSecondaryVisible">
          <div v-if="fetchingMarketerParametersSecondary" class="text-blue-400">
            <base-loading class="block text-3xl" />
            Loading additional parameters
          </div>
          <transition name="slide-in">
            <div
              v-if="!fetchingMarketerParametersSecondary"
              class="flex flex-wrap"
            >
              <CreateLinkMarketerParameter
                v-for="parameter in marketerParametersSecondary"
                :key="parameter.name"
                :parameter="parameter"
                @input="selectParameterSecondary"
                @reset="resetParameterSecondary"
                class="w-1/3 px-4 py-2"
              />
            </div>
          </transition>
        </template>
      </div>

      <div class="px-16 py-8 mb-8">
        <FormulateErrors />
        <p v-if="formHasErrors" class="text-red-600 mb-4">
          <icon-alert />
          Not all fields have been filled out correctly. Please check and try
          again.
        </p>
        <base-button
          type="submit"
          :loading="creatingLink"
          class="w-full text-xl"
        >
          Create Link
        </base-button>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import CreateLinkMarketerParameter from './components/CreateLinkMarketerParameter';
import { useMarketerParameters } from '@/composables/useMarketerParameters';
import { useFormValues } from '@/composables/useFormValues';
import { useLinkTemplate } from '@/composables/useLinkTemplate';
import { prepareLinkPayload } from './services/prepareLinkPayload';

export default Vue.extend({
  components: {
    CreateLinkMarketerParameter,
  },
  props: {
    linkId: Number,
    templateId: Number,
  },
  setup(props, { root }) {
    const { template, fetchingTemplate } = useLinkTemplate({ props, root });

    return {
      fetchingTemplate,
      ...useMarketerParameters(template),
      ...useFormValues({
        store: root.$store,
        template,
      }),
    };
  },
  data() {
    return {
      formName: 'create-link',
      formHasErrors: false,
      creatingLink: false,
    };
  },
  computed: {
    ...mapState({
      currentCompanyId: (state) => state.company.currentId,
      currentSegmentId: (state) => state.company.segment.currentId,
      changingSegment: (state) => state.company.segment.changingSegment,
      parameterNames: (state) => state.company.parameterNames,
    }),
  },
  methods: {
    scrollTo(id) {
      document.querySelector(`#${id}`).scrollIntoView({ behavior: 'smooth' });
    },
    async createLink(formData) {
      try {
        if (!this.marketerParametersCompleted) {
          throw {
            formErrors: ['Not all marketer parameters have been selected!'],
          };
        }
        this.creatingLink = true;
        this.$formulate.resetValidation(this.formName);
        const payload = prepareLinkPayload(
          formData,
          this.marketerParameters,
          this.marketerParametersSecondary,
          this.marketerSelections()
        );
        const { data } = await this.$api.post(
          `companies/${this.currentCompanyId}/segments/${this.currentSegmentId}/tracking_links/`,
          payload
        );
        this.$router.push({
          name: 'create-link-success',
          params: {
            link: data,
          },
        });
      } catch (error) {
        this.$formulate.handle(error, this.formName);
        console.error(error);
      } finally {
        this.creatingLink = false;
      }
    },
    async checkValidation(submission) {
      this.formHasErrors = await submission.hasValidationErrors();
    },
  },
});
</script>
